// Le formulaire d'une fiche profil. Un seul, pour les trois chemins qui y mènent : la
// saisie à la main, la relecture d'un CV lu par le modèle, et la modification d'une fiche
// existante. Trois formulaires qui divergent, c'est un champ qu'on ajoute à deux endroits
// sur trois — et une fiche qui ne veut plus dire la même chose selon la porte d'entrée.
import { $, brut, h, valeur, type Brut } from '../ui.ts';
import { type Referentiel } from '../etat.ts';
import { GROUPES_ZONES } from '../../domaine/lieux.ts';
import { TYPES_ENGAGEMENT } from '../../domaine/vivier.ts';
import { POSTURES_IA, PREUVE_POSTURE } from '../../domaine/ia.ts';
import { DIVISEUR_SALAIRE } from '../../domaine/grille.ts';

/**
 * Le canal, et rien d'autre. « Candidature spontanée » et « saisie » n'y figurent plus :
 * ce n'étaient pas des canaux mais des régimes, et les mélanger obligeait à répondre deux
 * questions dans une seule liste.
 */
export const CANAUX = ['LinkedIn', 'cooptation', 'Malt', 'APEC', 'Free-Work',
                       'site WAKEA', 'salon ou conférence', 'autre'];

/** Un canal enregistré avant cette liste ne doit pas se faire remplacer en silence. */
export const canaux = (courant?: string | null) =>
  courant && !CANAUX.includes(courant) ? [courant, ...CANAUX] : CANAUX;

export interface FicheProfil {
  prenom: string; nom: string; titre?: string | null; role_grille?: string | null;
  types: string[]; tjm_eur?: number | null;
  salaire_min_eur?: number | null; salaire_max_eur?: number | null;
  dispo_texte?: string | null; disponible_a_partir_de?: string | null;
  zones: string[]; mobilite?: string | null;
  expertises: string[]; secteurs: string[];
  ia_posture: string[]; ia_outils: string[]; ia_preuve?: string | null;
  linkedin?: string | null; email?: string | null; telephone?: string | null;
  origine?: string | null; collecte?: string | null;
  // Ce que le CV WAKEA compose. Vide, aucune de ces rubriques ne s'imprime.
  experience_depuis?: number | null;
  langues: { langue: string; niveau?: string | null }[];
  formations: { annee?: string | null; diplome: string; ecole?: string | null }[];
  certifications: string[];
}

export const FICHE_VIDE: FicheProfil = {
  prenom: '', nom: '', types: [], zones: [], expertises: [], secteurs: [], ia_outils: [],
  ia_posture: [], langues: [], formations: [], certifications: [],
};

/**
 * Compléter un dossier qui existe à partir d'un nouveau document, sans l'abîmer.
 *
 * Un CV dit rarement un taux journalier, une rémunération, une date de disponibilité ou
 * un numéro de téléphone. Écrire quand même ce que le modèle n'a pas lu revenait à
 * effacer ce qu'un consultant avait saisi — et c'est la pire forme de perte : silencieuse,
 * au moment précis où l'on croit enrichir le dossier.
 *
 * La règle tient en une phrase : **ce que le document ne dit pas ne s'écrit pas.** Le
 * formulaire de relecture part du dossier tel qu'il est, et la lecture ne recouvre que les
 * champs qu'elle a effectivement renseignés. Un champ lu, lui, remplace : un CV plus récent
 * qui donne un nouveau téléphone est une mise à jour, et c'est ce qu'on attend de lui.
 *
 * Le vide est entendu au sens large — chaîne vide, liste vide, objet vide — parce qu'un
 * modèle qui n'a rien trouvé rend `[]` aussi souvent que `null`, et que les deux veulent
 * dire la même chose : le document est muet sur ce point.
 */
const nonVide = (v: unknown): boolean => {
  if (v === null || v === undefined || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v as object).length > 0;
  return true;
};

export const fusionnerFiche = (dossier: Record<string, unknown>,
                               lue: Record<string, unknown>): FicheProfil => {
  const r: Record<string, unknown> = { ...FICHE_VIDE, ...dossier };
  for (const [cle, v] of Object.entries(lue)) if (nonVide(v)) r[cle] = v;
  return r as unknown as FicheProfil;
};

const etiquette = (libelle: string, cle: string, deduits: string[]) => h`
  <label class="lab" for="f-${cle}">${libelle}
    ${deduits.includes(cle) ? h`<span class="pt" style="color:var(--copper);letter-spacing:0">· déduit</span>` : ''}</label>`;

const champ = (libelle: string, cle: string, v: unknown, d: string[], type = 'text') => h`
  <div>${etiquette(libelle, cle, d)}
    <input class="champ" id="f-${cle}" type="${type}" value="${v ?? ''}"></div>`;

const pave = (libelle: string, cle: string, v: string[] | string | undefined, d: string[], rangs = 3) => h`
  <div>${etiquette(libelle, cle, d)}
    <textarea class="champ" id="f-${cle}" rows="${rangs}">${Array.isArray(v) ? v.join('\n') : v ?? ''}</textarea></div>`;

const listeChamp = (libelle: string, cle: string, v: string[], d: string[]) =>
  champ(libelle, cle, v.join(', '), d);

/**
 * Découpe une liste saisie sur une ligne — mais pas sur les virgules entre parenthèses.
 *
 * « Conduite du changement (direction générale, métiers) » est **une** expertise, pas deux.
 * Le champ se remplit par `join(', ')` et se relit par ce découpage : sans cette règle,
 * l'aller-retour n'est pas réversible. Rouvrir une fiche lue dans un CV, sans rien y
 * toucher, fabriquait une entrée de plus à chaque parenthèse — et finissait par franchir
 * le plafond de trente expertises. La fiche était alors refusée sur une ligne que personne
 * n'avait modifiée, ce qui est le refus le plus difficile à comprendre qui soit.
 *
 * Les crochets comptent comme des parenthèses : « Data (NLP, vision) [R&D] » se rencontre
 * dans les CV, et rien ne justifie de traiter les deux différemment.
 */
export function decouperListe(v: string): string[] {
  const sortie: string[] = [];
  let courant = '';
  let profondeur = 0;
  for (const c of v) {
    if (c === '(' || c === '[') profondeur += 1;
    else if (c === ')' || c === ']') profondeur = Math.max(0, profondeur - 1);
    if (c === ',' && profondeur === 0) { sortie.push(courant); courant = ''; continue; }
    courant += c;
  }
  sortie.push(courant);
  return sortie.map((x) => x.trim()).filter(Boolean);
}

/**
 * Un lien LinkedIn se saisit de six façons : avec ou sans protocole, avec ou sans « www »,
 * en http, depuis une version régionale du site, ou réduit à l'identifiant. Le serveur,
 * lui, n'accepte qu'une URL. Plutôt que de refuser la saisie, on la remet en forme.
 */
export function normaliserLien(v: string | null | undefined): string | null {
  let t = (v ?? '').trim().replace(/\s+/g, '');
  if (!t) return null;
  // Un identifiant seul — ni point, ni barre — ne peut être qu'un profil LinkedIn ici.
  if (!t.includes('.') && !t.includes('/')) return `https://www.linkedin.com/in/${t}`;
  t = t.replace(/^\/+/, '');
  if (!/^https?:\/\//i.test(t)) t = `https://${t}`;
  t = t.replace(/^http:\/\//i, 'https://');
  t = t.replace(/^https:\/\/(?:[a-z]{2}\.|www\.)?linkedin\.com/i, 'https://www.linkedin.com');
  return t;
}

/**
 * Un numéro français se lit « +33 6 12 34 56 78 » et se tape de dix façons. On normalise
 * ce qu'on sait reconnaître et on laisse le reste tel quel : un numéro étranger mal
 * regroupé vaut mieux qu'un numéro français imposé à un consultant marocain.
 */
export function normaliserTelephone(v: string | null | undefined): string | null {
  const brut = (v ?? '').trim();
  if (!brut) return null;
  let t = brut.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');
  if (!t) return brut;
  if (t.startsWith('00')) t = `+${t.slice(2)}`;
  if (/^0\d{9}$/.test(t)) t = `+33${t.slice(1)}`;
  if (/^\+33\d{9}$/.test(t)) {
    const r = t.slice(3);
    return `+33 ${r[0]} ${(r.slice(1).match(/.{2}/g) ?? []).join(' ')}`;
  }
  // Numéro étranger : on ne connaît pas son découpage, et le nôtre serait un contresens.
  // On rend ce qui a été tapé, les espaces normalisés — lisible vaut mieux que reformaté.
  return brut.replace(/\s+/g, ' ');
}

/** Équivalent en taux journalier, tel que l'écran l'affiche. Même règle que le serveur. */
export const tjEquivalentTexte = (min: number | null, max: number | null): string => {
  if (min == null && max == null) return '';
  const bas = Math.round((min ?? max!) / DIVISEUR_SALAIRE);
  const haut = Math.round((max ?? min!) / DIVISEUR_SALAIRE);
  return bas === haut ? `≈ ${bas} € par jour` : `≈ ${bas} – ${haut} € par jour`;
};

export function formulaireProfil(f: FicheProfil, ref: Referentiel, deduits: string[] = [],
                                 avecOrigine = true): Brut {
  const d = deduits;
  return h`
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
      ${champ('PRÉNOM', 'prenom', f.prenom, d)}
      ${champ('NOM', 'nom', f.nom, d)}
    </div>
    ${champ('TITRE PORTÉ PAR LA PERSONNE', 'titre', f.titre, d)}

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
      <div>${etiquette('RÔLE · GRILLE WAKEA', 'role_grille', d)}
        <select class="champ" id="f-role_grille"><option value="">—</option>
          ${ref.roles.map((r) => h`<option value="${r.cle}" ${r.cle === f.role_grille ? brut('selected') : ''}>${r.libelle}</option>`)}</select></div>
      <div><label class="lab">FORME · PLUSIEURS POSSIBLES</label>
        <div class="rang" style="gap:5px">${TYPES_ENGAGEMENT.map((t) =>
          h`<label class="puce"><input type="checkbox" name="f-type" value="${t}"
            ${f.types.includes(t) ? brut('checked') : ''}> ${t}</label>`)}</div></div>
    </div>

    <div class="carte" style="background:#FDFCFB">
      <div class="sur">CONDITIONS</div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-top:9px">
        ${champ('TAUX JOURNALIER DEMANDÉ', 'tjm_eur', f.tjm_eur, d, 'number')}
        ${champ('SALAIRE ANNUEL BRUT · MINIMUM', 'salaire_min_eur', f.salaire_min_eur, d, 'number')}
        ${champ('SALAIRE ANNUEL BRUT · MAXIMUM', 'salaire_max_eur', f.salaire_max_eur, d, 'number')}
      </div>
      <p class="pt" id="f-tj" style="margin:7px 0 0;color:var(--copper);min-height:14px">
        ${tjEquivalentTexte(f.salaire_min_eur ?? null, f.salaire_max_eur ?? null)}</p>
      <p class="pt sourd" style="margin:4px 0 0">Un salaire se lit ${DIVISEUR_SALAIRE} fois plus petit
        à la journée — c'est cet équivalent qui sert à comparer un salarié et un indépendant,
        et à calculer la marge en régie.</p>
    </div>

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
      ${champ('DISPONIBILITÉ, TELLE QU’ELLE EST DITE', 'dispo_texte', f.dispo_texte, d)}
      ${champ('LIBRE À PARTIR DU', 'disponible_a_partir_de', f.disponible_a_partir_de, d, 'date')}
    </div>
    <p class="pt sourd" style="margin:-4px 0 0">La date est ce que le rapprochement compare au
      démarrage d'une mission. La formule libre garde la nuance. La fraîcheur, elle, se date seule.</p>

    <div><label class="lab">ZONES D’INTERVENTION
      ${d.includes('zones') ? h`<span class="pt" style="color:var(--copper);letter-spacing:0">· déduit</span>` : ''}</label>
      ${Object.entries(GROUPES_ZONES).map(([groupe, zones]) => h`
        <div style="margin-top:7px"><div class="pt sourd" style="letter-spacing:1.2px">${groupe.toUpperCase()}</div>
          <div class="rang" style="flex-wrap:wrap;gap:5px;margin-top:4px">
            ${zones.map((z) => h`<label class="puce"><input type="checkbox" name="f-zone" value="${z}"
              ${f.zones.includes(z) ? brut('checked') : ''}> ${z}</label>`)}</div></div>`)}
    </div>
    ${champ('MOBILITÉ · PRÉCISION LIBRE', 'mobilite', f.mobilite, d)}

    ${listeChamp('EXPERTISES · SÉPARÉES PAR DES VIRGULES', 'expertises', f.expertises, d)}
    ${listeChamp('SECTEURS · SÉPARÉS PAR DES VIRGULES', 'secteurs', f.secteurs, d)}

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
      <div>${etiquette('POSTURES IA', 'ia_posture', d)}
        <div class="rang" style="flex-wrap:wrap;gap:5px" id="f-ia_posture">
          ${POSTURES_IA.map((p) => h`<button type="button" class="puce" data-p="${p}"
            aria-pressed="${(f.ia_posture ?? []).includes(p)}"
            title="${PREUVE_POSTURE[p]}">${p}</button>`)}</div>
        <p class="pt sourd" style="margin:4px 0 0">Plusieurs sont possibles : ce n'est pas une échelle.</p></div>
      ${listeChamp('OUTILS IA CITÉS', 'ia_outils', f.ia_outils, d)}
    </div>
    <div>${etiquette('CE QUI PROUVE LA POSTURE', 'ia_preuve', d)}
      <textarea class="champ" id="f-ia_preuve" rows="2">${f.ia_preuve ?? ''}</textarea></div>

    <div class="carte" style="background:#FDFCFB">
      <div class="sur">CE QUE LE CV WAKEA COMPOSE</div>
      <p class="pt sourd" style="margin:6px 0 0">Ces quatre rubriques ne servent qu'au document
        qu'on envoie au client. Vides, elles ne s'impriment pas — une rubrique absente vaut
        mieux qu'une rubrique inventée.</p>
      <div style="margin-top:10px;display:flex;flex-direction:column;gap:12px">
        ${champ('ANNÉE DU PREMIER POSTE', 'experience_depuis', f.experience_depuis, d, 'number')}
        ${pave('LANGUES · UNE PAR LIGNE, « LANGUE | NIVEAU »', 'langues',
               f.langues.map((l) => (l.niveau ? `${l.langue} | ${l.niveau}` : l.langue)), d, 3)}
        ${pave('FORMATIONS · UNE PAR LIGNE, « ANNÉE | DIPLÔME | ÉCOLE »', 'formations',
               f.formations.map((x) => [x.annee ?? '', x.diplome, x.ecole ?? ''].join(' | ')), d, 3)}
        ${pave('CERTIFICATIONS · UNE PAR LIGNE', 'certifications', f.certifications, d, 3)}
      </div>
    </div>

    <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px">
      ${champ('LIEN LINKEDIN', 'linkedin', f.linkedin, d, 'url')}
      ${champ('COURRIEL', 'email', f.email, d, 'email')}
      ${champ('TÉLÉPHONE', 'telephone', f.telephone, d)}
    </div>

    ${avecOrigine ? h`<div class="carte" style="background:#FDFCFB">
      <div class="sur">PROVENANCE</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:9px">
        <div><label class="lab" for="f-collecte">QUI A FAIT LE PREMIER PAS ?</label>
          <select class="champ" id="f-collecte">
            <option value="indirecte" ${f.collecte !== 'directe' ? brut('selected') : ''}>Nous — profil repéré par le cabinet</option>
            <option value="directe" ${f.collecte === 'directe' ? brut('selected') : ''}>La personne — elle nous a contactés</option>
          </select></div>
        <div><label class="lab" for="f-origine">PAR QUEL CANAL ?</label>
          <select class="champ" id="f-origine">
            ${canaux(f.origine).map((o) => h`<option value="${o}" ${o === f.origine ? brut('selected') : ''}>${o}</option>`)}</select></div>
      </div>
      <p class="pt sourd" style="margin:7px 0 0">Ces deux réponses ne disent pas la même chose, et la
        première seule décide du régime. Une personne qui vous a écrit sur LinkedIn doit être informée
        au moment de l'enregistrement ; une personne que vous y avez repérée, dans le mois — même si
        vous ne la contactez jamais. MANA envoie la notice dans les deux cas, dès la création.</p>
    </div>` : ''}`;
}

/**
 * Ce qui se recalcule ou se remet en forme pendant la saisie. L'équivalent journalier
 * suit la frappe ; le lien et le téléphone attendent qu'on quitte le champ — corriger
 * sous les doigts de quelqu'un qui tape est la meilleure façon de l'empêcher d'écrire.
 */
export function brancherFiche(): void {
  // Les postures se cochent et se décochent : plusieurs peuvent être vraies à la fois.
  $('#f-ia_posture')?.addEventListener('click', (ev) => {
    const puce = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-p]');
    if (!puce) return;
    puce.setAttribute('aria-pressed', puce.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
  });

  const rafraichir = () => {
    const n = $('#f-tj');
    if (!n) return;
    const lire = (cle: string) => { const v = valeur(`#f-${cle}`); return v ? Number(v) : null; };
    n.textContent = tjEquivalentTexte(lire('salaire_min_eur'), lire('salaire_max_eur'));
  };
  for (const cle of ['salaire_min_eur', 'salaire_max_eur'])
    $(`#f-${cle}`)?.addEventListener('input', rafraichir);

  const remettreEnForme = (cle: string, f: (v: string) => string | null) => {
    const champ = $(`#f-${cle}`) as HTMLInputElement | null;
    champ?.addEventListener('blur', () => { champ.value = f(champ.value) ?? ''; });
  };
  remettreEnForme('linkedin', normaliserLien);
  remettreEnForme('telephone', normaliserTelephone);
}

export function recolterProfil(): FicheProfil {
  const t = (cle: string) => valeur(`#f-${cle}`) || null;
  const n = (cle: string) => {
    // « 45 000 » se tape aussi naturellement que « 45000 », et l'espace insécable arrive
    // tout seul par copier-coller. Number() en ferait un NaN, que zod refuserait sans
    // dire lequel des trois champs de conditions est en cause.
    const v = valeur(`#f-${cle}`).replace(/[\s\u00a0\u202f]/g, '').replace(/[^\d.-]/g, '');
    if (!v) return null;
    const nombre = Number(v);
    return Number.isFinite(nombre) ? Math.round(nombre) : null;
  };
  const separe = (cle: string) => decouperListe(valeur(`#f-${cle}`));
  const coches = (nom: string) =>
    [...document.querySelectorAll<HTMLInputElement>(`input[name="${nom}"]:checked`)].map((x) => x.value);
  const lignes = (cle: string) =>
    ((document.querySelector<HTMLTextAreaElement>(`#f-${cle}`))?.value ?? '')
      .split('\n').map((x) => x.trim()).filter(Boolean);
  const morceaux = (ligne: string) => ligne.split('|').map((x) => x.trim());

  return {
    prenom: t('prenom') ?? '', nom: t('nom') ?? '', titre: t('titre'),
    role_grille: t('role_grille'), types: coches('f-type'),
    tjm_eur: n('tjm_eur'), salaire_min_eur: n('salaire_min_eur'), salaire_max_eur: n('salaire_max_eur'),
    dispo_texte: t('dispo_texte'), disponible_a_partir_de: t('disponible_a_partir_de'),
    zones: coches('f-zone'), mobilite: t('mobilite'),
    expertises: separe('expertises'), secteurs: separe('secteurs'),
    // Les puces cochées, et non la valeur d'un champ : plusieurs postures coexistent.
    ia_posture: [...document.querySelectorAll<HTMLElement>('#f-ia_posture [data-p][aria-pressed="true"]')]
      .map((x) => x.dataset['p']!),
    ia_outils: separe('ia_outils'), ia_preuve: t('ia_preuve'),
    // Normalisés une seconde fois ici : le champ peut n'avoir jamais perdu le focus,
    // et une saisie collée puis validée au clavier n'aurait pas été reprise.
    linkedin: normaliserLien(t('linkedin')), email: t('email'),
    telephone: normaliserTelephone(t('telephone')),
    experience_depuis: n('experience_depuis'),
    // « Français | Langue maternelle » : la barre verticale, parce qu'elle n'apparaît
    // jamais dans un nom de langue ni dans un intitulé de diplôme.
    langues: lignes('langues').map((l) => {
      const [langue, niveau] = morceaux(l);
      return { langue: langue ?? '', niveau: niveau || null };
    }).filter((l) => l.langue),
    formations: lignes('formations').map((l) => {
      const [annee, diplome, ecole] = morceaux(l);
      return { annee: annee || null, diplome: diplome ?? '', ecole: ecole || null };
    }).filter((x) => x.diplome),
    certifications: lignes('certifications'),
    origine: t('origine') ?? 'autre', collecte: t('collecte') ?? 'indirecte',
  };
}

/** La couche qui porte ce formulaire. La même pour créer et pour modifier. */
export function coucheFiche(titre: string, sur: string, f: FicheProfil, ref: Referentiel,
                            forme: string, action: string, avecOrigine: boolean): Brut {
  return h`
    <div class="voile" data-act="fermer-voile"><div class="couche large-c">
      <div class="ct"><div><div class="sur">${sur}</div><h2>${titre}</h2></div>
        <button class="x" data-act="fermer" aria-label="Fermer">✕</button></div>
      <form class="cc" id="${forme}">
        ${formulaireProfil(f, ref, [], avecOrigine)}
        <p class="err" id="err" hidden></p>
      </form>
      <div class="cp"><button class="bt" data-act="fermer">Annuler</button>
        <button class="bt p" form="${forme}" type="submit">${action}</button></div>
    </div></div>`;
}
